import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from '../../../utils/dateUtils';
import MemberCard from './MemberCard';

const MatchDetails = ({ event, members, user }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8">
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          {t("player.matchDetails")}
        </h3>
        
        {/* Match Status Banner */}
        {event.eventStatus === 'Completed' && event.metaData?.winner && (
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl p-4 mb-6 text-center">
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              <h3 className="text-xl font-bold text-white">
                {t("player.matchCompleted", "Match Completed")}
              </h3>
            </div>
            <p className="text-white mt-2">
              🏆 {t("player.winner", "Winner")}: {
                event.metaData.winner === 'team1' ? event.team1?.name : 
                event.metaData.winner === 'team2' ? event.team2?.name : 
                t("player.draw", "Draw")
              }
            </p>
          </div>
        )}

        {event.eventStatus === 'In Progress' && (
          <div className="bg-gradient-to-r from-green-400 to-blue-500 rounded-xl p-4 mb-6 text-center">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
              <h3 className="text-xl font-bold text-white">
                {t("player.matchLive", "Match Live")}
              </h3>
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            </div>
          </div>
        )}

        {/* Match Header with Team Logos and Score */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            {/* Team 1 */}
            <div className="flex flex-col items-center space-y-3 flex-1">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-white shadow-lg border-4 border-white">
                {event.team1?.logo ? (
                  <img
                    src={event.team1.logo}
                    alt={event.team1?.name || 'Team 1'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-blue-500 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="text-center">
                <h4 className="text-lg font-bold text-gray-900 mb-1">
                  {event.team1?.name || 'Team 1'}
                </h4>
                <p className="text-sm text-gray-600 capitalize">
                  {event.team1?.sport || 'Sport'} • {event.team1?.type || 'Team'}
                </p>
              </div>
              {event.metaData?.score?.team1 !== undefined && (
                <div className="text-4xl font-bold text-blue-600 bg-white rounded-lg px-4 py-2 shadow">
                  {event.metaData.score.team1}
                </div>
              )}
            </div>
            
            {/* VS Divider with Central Score */}
            <div className="flex flex-col items-center mx-6">
              {/* Central Scoreboard */}
              {event.metaData?.score && (event.metaData.score.team1 !== undefined && event.metaData.score.team2 !== undefined) ? (
                <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-200 mb-4">
                  <div className="text-center mb-2">
                    <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Score</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-3xl font-bold text-blue-600">
                      {event.metaData.score.team1}
                    </div>
                    <div className="text-lg text-gray-400">-</div>
                    <div className="text-3xl font-bold text-green-600">
                      {event.metaData.score.team2}
                    </div>
                  </div>
                  {event.eventStatus === 'In Progress' && (
                    <div className="text-xs text-green-600 font-medium mt-1 animate-pulse">
                      LIVE
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-full p-4 shadow-lg border-2 border-gray-100">
                  <span className="text-lg font-bold text-gray-600">VS</span>
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2 text-center">
                {format(new Date(event.startDate), 'MMM dd, HH:mm')}
              </div>
            </div>
            
            {/* Team 2 */}
            <div className="flex flex-col items-center space-y-3 flex-1">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-white shadow-lg border-4 border-white">
                {event.team2?.logo ? (
                  <img
                    src={event.team2.logo}
                    alt={event.team2?.name || 'Team 2'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-green-500 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="text-center">
                <h4 className="text-lg font-bold text-gray-900 mb-1">
                  {event.team2?.name || 'Team 2'}
                </h4>
                <p className="text-sm text-gray-600 capitalize">
                  {event.team2?.sport || 'Sport'} • {event.team2?.type || 'Team'}
                </p>
              </div>
              {event.metaData?.score?.team2 !== undefined && (
                <div className="text-4xl font-bold text-green-600 bg-white rounded-lg px-4 py-2 shadow">
                  {event.metaData.score.team2}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Teams Container */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Team 1 Members */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center space-x-3 bg-white rounded-full px-4 py-2 shadow-md">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-blue-400">
                  {event.team1?.logo ? (
                    <img
                      src={event.team1.logo}
                      alt={event.team1?.name || 'Team 1'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <h4 className="text-lg font-bold text-blue-800">
                    {event.team1?.name || 'Team 1'}
                  </h4>
                  <p className="text-sm text-blue-600">
                    {members.filter(member => member.teamId === event.team1?._id).length} {t("player.members")}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              {members.filter(member => member.teamId === event.team1?._id).map((member) => {
                const isCurrentUser = member.userId === user?.id || 
                                     member.userId === user?._id || 
                                     member.userId === user?.metaData?.userId;
                
                return (
                  <MemberCard
                    key={member.userId}
                    member={member}
                    user={user}
                    isCurrentUser={isCurrentUser}
                  />
                );
              })}
            </div>
          </div>

          {/* Team 2 Members */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center space-x-3 bg-white rounded-full px-4 py-2 shadow-md">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-green-400">
                  {event.team2?.logo ? (
                    <img
                      src={event.team2.logo}
                      alt={event.team2?.name || 'Team 2'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-green-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <h4 className="text-lg font-bold text-green-800">
                    {event.team2?.name || 'Team 2'}
                  </h4>
                  <p className="text-sm text-green-600">
                    {members.filter(member => member.teamId === event.team2?._id).length} {t("player.members")}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {members.filter(member => member.teamId === event.team2?._id).map((member) => {
                const isCurrentUser = member.userId === user?.id || 
                                     member.userId === user?._id || 
                                     member.userId === user?.metaData?.userId;
                
                return (
                  <MemberCard
                    key={member.userId}
                    member={member}
                    user={user}
                    isCurrentUser={isCurrentUser}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchDetails; 